import isFunction from 'lodash/isFunction';
import React, { useCallback, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useDispatch, useSelector } from 'react-redux';
import { OverlayShareLink } from 'src/components/common/Overlay/OverlayShareLink';

import { UiModalCommonButtons } from 'src/components/UI/modal/_buttons';
import { UiModal } from 'src/components/UI/modal/UiModal';
import { commonSelector } from 'src/store/commonPart/selector';
import { hideModal } from 'src/store/commonPart/slice';
import { getUniqueId } from 'src/utils/getUniqueId';
import { ChildrenPropType } from 'src/utils/types';

type ContextTypeData = {
  data: {
    body: string | React.ReactElement;

    header?: string;
    successBtnText?: UiModalCommonButtons['successBtnText'];
    cancelBtnText?: UiModalCommonButtons['cancelBtnText'];
    singleButton?: boolean;
    manualClosing?: boolean;
    disableClose?: boolean;
    successBtnOnClick?: UiModalCommonButtons['successBtnOnClick'];
    cancelBtnOnClick?: UiModalCommonButtons['cancelBtnOnClick'];
  };
  id: string;

  onSuccess?: () => void;
  onCancel?: () => void;
};

type ContextType = (
  data: ContextTypeData['data'],
  onSuccess?: ContextTypeData['onSuccess'],
  onCancel?: ContextTypeData['onCancel']
) => void;

type Props = {
  children: ChildrenPropType;
};

/** contains a function which calls a simple confirm window */
export const ConfirmContext = React.createContext<ContextType>(() => undefined);
ConfirmContext.displayName = 'ConfirmContext';

export const CommonLogic = ({ children }: Props): React.ReactElement => {
  const dispatch = useDispatch();

  const { modal, overlayShareLink } = useSelector(commonSelector);

  const { t } = useTranslation();

  const onHideModal = useCallback(
    (id: string) => () => {
      dispatch(hideModal(id));
    },
    [dispatch]
  );

  const [confirmModalsData, setConfirmModalsData] = useState<ContextTypeData[]>([]);

  const ref = useRef<string | null>(null);

  /** context */
  const confirm: ContextType = useCallback((data, onSuccess, onCancel) => {
    const id = getUniqueId();

    setConfirmModalsData((v) => [
      ...v,
      {
        data,
        onSuccess,
        onCancel,
        id,
      },
    ]);
  }, []);
  /** end context */

  return (
    <ConfirmContext.Provider value={confirm}>
      {children}

      {confirmModalsData.map(({ id, data, onSuccess, onCancel }) => (
        <UiModal
          key={id}
          header={data.header || t('common.attention')}
          disableClose={data.disableClose}
          modalClassName="ws-pw"
          /* eslint-disable-next-line react/no-unstable-nested-components */
          customFooter={(close) => (
            <UiModalCommonButtons
              close={close}
              cancelBtnText={data.cancelBtnText}
              successBtnText={data.successBtnText || t('common.ok')}
              onCancel={() => {
                ref.current = id;
              }}
              successBtnOnClick={data.successBtnOnClick}
              cancelBtnOnClick={data.cancelBtnOnClick}
              onSuccess={() => {
                if (isFunction(onSuccess)) {
                  onSuccess();
                }

                close();
              }}
              onlySuccessBtn={data.singleButton}
            />
          )}
          onHide={() => {
            setConfirmModalsData((v) => v.filter((item) => item.id !== id));

            if (isFunction(onCancel) && (!data.manualClosing || ref.current === id)) {
              onCancel();
            }

            ref.current = null;
          }}
        >
          {data.body}
        </UiModal>
      ))}

      {modal.map((modalItem) => (
        // eslint-disable-next-line react/jsx-props-no-spreading
        <UiModal key={modalItem.id} {...modalItem} {...modalItem.buttonFooter} onHide={onHideModal(modalItem.id)}>
          {modalItem.body}
        </UiModal>
      ))}

      {overlayShareLink && <OverlayShareLink text={overlayShareLink} />}
    </ConfirmContext.Provider>
  );
};
